/**
 * NC-1258: countdown for a backend dispatch lock (runs-verify, runs-materials-sync).
 *
 * Returns the age of the lock in seconds while it is still held, and `null` as
 * soon as it has expired (or when there is no/an unparsable timestamp). Callers
 * disable their dispatch button on a non-null value.
 *
 * Why a hook and not a `useMemo`: the naive version froze `Date.now()` at memo
 * evaluation with only `[requestedUtc]` in the dep list, so nothing ever
 * recomputed it. That was self-jamming — the only writer of `requestedUtc` is
 * the dispatch that the disabled button blocks — and the panel's parent poll
 * did not help, because the timestamp string stays identical across polls.
 *
 * This ticks once a second while the lock is held so the displayed age stays
 * honest, and stops itself the moment the lock expires: no timer runs when
 * there is nothing counting down.
 */

import { useEffect, useState } from 'react';

export function useExpiringLock(
  requestedUtc: string | undefined,
  lockMs: number
): number | null {
  const [now, setNow] = useState(() => Date.now());

  const startedMs = requestedUtc ? Date.parse(requestedUtc) : NaN;
  const valid = Number.isFinite(startedMs);
  const expiresAtMs = valid ? startedMs + lockMs : 0;

  useEffect(() => {
    // No timestamp, garbage timestamp, or an already-expired lock: nothing to
    // count down, so never arm a timer.
    if (!valid || Date.now() >= expiresAtMs) return undefined;

    const id = setInterval(() => {
      const t = Date.now();
      setNow(t);
      // Self-clear at expiry — the deps below cannot change on expiry, so
      // without this the interval would keep ticking for the page's lifetime.
      if (t >= expiresAtMs) clearInterval(id);
    }, 1000);

    // Also covers unmount-before-expiry: no setState on an unmounted component.
    return () => clearInterval(id);
  }, [valid, expiresAtMs]);

  if (!valid || now >= expiresAtMs) return null;
  return Math.max(1, Math.floor((now - startedMs) / 1000));
}

export default useExpiringLock;
